// src/components/tasks/BlockCreateDialog.tsx
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { Paperclip, X } from "lucide-react"
import { useState } from "react"
import { toast } from "@/hooks/use-toast"
import { useAuthStore } from "@/store/authStore"
import { db, storage } from "@/lib/firebase"
import { addDoc, collection, serverTimestamp, updateDoc, doc, arrayUnion } from "firebase/firestore"
import { uploadBytesResumable, getDownloadURL, ref as storageRef } from "firebase/storage"
import { useCalendarEventStore } from "@/store/calendarEventStore"

// === ДИЗАЙН-СЛОЙ ===
import { blockCreateTheme } from "./ui/BlockCreateDialogUI"

const employees = [
  { id: 1, name: "Андреев Алексей Евгеньевич" },
  { id: 2, name: "Багаутдинов Ахмет Айратович" },
  { id: 3, name: "Барладян Ирина Алексеевна" },
  { id: 4, name: "Павлик Кристина Валентиновна" },
  { id: 5, name: "Султанова Диана Рашитовна" },
  { id: 6, name: "Казарин Сергей Константинович" },
  { id: 7, name: "Степанушкин Сергей Вадимович" },
]

export default function BlockCreateDialog() {
  const { user } = useAuthStore()
  const { addEvent } = useCalendarEventStore()

  const [open, setOpen] = useState(false)
  const [newBlock, setNewBlock] = useState({
    title: "",
    description: "",
    assigneeId: 0,
    coAssigneeIds: [] as number[],
    observerIds: [] as number[],
    deadline: "",
    urgency: "planned" as "urgent" | "planned",
    difficulty: "medium" as "easy" | "medium" | "hard",
    reminderInterval: "",
  })
  const [newBlockFile, setNewBlockFile] = useState<File | null>(null)

  const userId = user ? Number(user.id) : 0

  const canAssignTo = (assigneeId: number): boolean => {
    if (assigneeId === userId) return true
    if (userId === 1) return true
    if (userId === 2) return assigneeId !== 1
    if (userId === 3 || userId === 4) return [3, 4, 5, 6, 7].includes(assigneeId)
    return false
  }

  const uploadFileAndGetData = async (file: File | null) => {
    if (!file) return null
    try {
      const fileRef = storageRef(storage, `tasks/${Date.now()}_${file.name}`)
      const uploadTask = uploadBytesResumable(fileRef, file)
      await new Promise((resolve, reject) => {
        uploadTask.on("state_changed", null, reject, resolve)
      })
      const url = await getDownloadURL(uploadTask.snapshot.ref)
      return { name: file.name, url }
    } catch (error) {
      toast({ title: "Ошибка загрузки файла", variant: "destructive" })
      throw error
    }
  }

  const createBlock = async () => {
    if (!user || !newBlock.title || newBlock.assigneeId === 0 || !newBlock.deadline) {
      toast({ title: "Ошибка", description: "Заполните все обязательные поля", variant: "destructive" })
      return
    }

    if (!canAssignTo(newBlock.assigneeId)) {
      toast({ title: "Ошибка", description: "Вы не можете ставить задачу этому сотруднику", variant: "destructive" })
      return
    }

    if (newBlock.coAssigneeIds?.length > 0) {
      for (const coId of newBlock.coAssigneeIds) {
        if (!canAssignTo(coId)) {
          toast({ title: "Ошибка", description: "Вы не можете назначить этого соисполнителя", variant: "destructive" })
          return
        }
        if (coId === newBlock.assigneeId) {
          toast({ title: "Ошибка", description: "Соисполнитель не может быть тем же, что и исполнитель", variant: "destructive" })
          return
        }
      }
    }

    if (newBlock.observerIds?.length > 0) {
      for (const obsId of newBlock.observerIds) {
        if (obsId === userId) {
          toast({ title: "Ошибка", description: "Нельзя добавить себя в наблюдатели", variant: "destructive" })
          return
        }
      }
    }

    const fileData = await uploadFileAndGetData(newBlockFile)

    const coAssignees = newBlock.coAssigneeIds
      .map((id) => employees.find((e) => e.id === id)?.name || "")
      .filter(Boolean)

    const observers = newBlock.observerIds
      .map((id) => employees.find((e) => e.id === id)?.name || "")
      .filter(Boolean)

    const isSelfTask = newBlock.assigneeId === userId

    try {
      const blockRef = await addDoc(collection(db, "tasks"), {
        title: newBlock.title,
        description: newBlock.description,
        assigneeId: newBlock.assigneeId,
        assignee: employees.find((e) => e.id === newBlock.assigneeId)?.name || "",
        coAssigneeIds: newBlock.coAssigneeIds || [],
        coAssignees,
        observerIds: newBlock.observerIds || [],
        observers,
        urgency: newBlock.urgency,
        difficulty: newBlock.difficulty,
        reminderInterval: newBlock.reminderInterval,
        deadline: newBlock.deadline,
        status: "new",
        approvalStatus: "new",
        approvalCount: 0,
        file: fileData,
        acceptedByExecutor: false,
        approvedBy: [],
        approvedByCreator: false,
        isBlock: true,
        createdAt: serverTimestamp(),
        createdBy: user.name,
        createdById: user.id,
        isSelfTask,
        history: [
          {
            userId: user.id,
            userName: user.name,
            action: "created",
            date: new Date().toISOString(),
          },
        ],
        messages: [],
      })

      await updateDoc(doc(db, "tasks", blockRef.id), {
        unreadBy: arrayUnion(
          newBlock.assigneeId,
          ...newBlock.coAssigneeIds,
          ...newBlock.observerIds
        )
      })

      toast({ 
        title: isSelfTask ? "Свой блок задач создан!" : "Блок создан!" 
      })

      if (newBlock.deadline) {
        try {
          await addEvent({
            date: newBlock.deadline,
            title: newBlock.title,
            type: "task",
            description: newBlock.description || "",
            createdBy: user.id.toString(),
            sourceTaskId: blockRef.id,
          })
          toast({ title: "Блок добавлен в календарь" })
        } catch (calendarErr) {
          console.error("Ошибка добавления блока в календарь:", calendarErr)
          toast({
            title: "Блок создан, но не добавлен в календарь",
            description: "Проверьте консоль",
            variant: "destructive",
          })
        }
      }

      setOpen(false)
      setNewBlock({
        title: "",
        description: "",
        assigneeId: 0,
        coAssigneeIds: [],
        observerIds: [],
        deadline: "",
        urgency: "planned",
        difficulty: "medium",
        reminderInterval: "",
      })
      setNewBlockFile(null)
    } catch (error) {
      console.error("Ошибка создания блока:", error)
      toast({ title: "Ошибка", description: "Не удалось создать блок", variant: "destructive" })
    }
  }

  const addCoAssignee = (coId: number) => {
    if (!newBlock.coAssigneeIds.includes(coId) && coId !== newBlock.assigneeId) {
      setNewBlock({ ...newBlock, coAssigneeIds: [...newBlock.coAssigneeIds, coId] })
    }
  }

  const removeCoAssignee = (coId: number) => {
    setNewBlock({ ...newBlock, coAssigneeIds: newBlock.coAssigneeIds.filter(id => id !== coId) })
  }

  const addObserver = (obsId: number) => {
    if (!newBlock.observerIds.includes(obsId) && obsId !== userId) {
      setNewBlock({ ...newBlock, observerIds: [...newBlock.observerIds, obsId] })
    }
  }

  const removeObserver = (obsId: number) => {
    setNewBlock({ ...newBlock, observerIds: newBlock.observerIds.filter(id => id !== obsId) })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className={blockCreateTheme.triggerButton}>
          + Блок задач
        </Button>
      </DialogTrigger>

      <DialogContent className={blockCreateTheme.dialogContent}>
        <DialogHeader>
          <DialogTitle className="text-2xl">Новый блок задач</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Наименование блока</Label>
            <Input
              value={newBlock.title}
              onChange={(e) => setNewBlock({ ...newBlock, title: e.target.value })}
              className={blockCreateTheme.formInput}
            />
          </div>

          <div>
            <Label>Подробное описание</Label>
            <Textarea
              value={newBlock.description}
              onChange={(e) => setNewBlock({ ...newBlock, description: e.target.value })}
              className={blockCreateTheme.formTextarea}
              rows={4}
            />
          </div>

          <div>
            <Label>Исполнитель</Label>
            <Select onValueChange={(v) => setNewBlock({ ...newBlock, assigneeId: +v })}>
              <SelectTrigger className={blockCreateTheme.selectTrigger}>
                <SelectValue placeholder="Выберите сотрудника" />
              </SelectTrigger>
              <SelectContent className={blockCreateTheme.selectContent}>
                {employees
                  .filter((e) => canAssignTo(e.id))
                  .map((e) => (
                    <SelectItem 
                      key={e.id} 
                      value={e.id.toString()} 
                      className="hover:bg-purple-800/60 text-purple-200"
                    >
                      {e.name}
                      {e.id === userId && (
                        <span className="ml-2 text-emerald-400 text-xs font-medium">(себе)</span>
                      )}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Соисполнители (не обязательно)</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {newBlock.coAssigneeIds.map((coId) => {
                const emp = employees.find(e => e.id === coId)
                return (
                  <Badge key={coId} className={blockCreateTheme.badgeCoAssignee}>
                    {emp?.name}
                    <button onClick={() => removeCoAssignee(coId)} className="ml-2">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )
              })}
            </div>
            <Select onValueChange={(v) => addCoAssignee(+v)}>
              <SelectTrigger className={blockCreateTheme.selectTrigger}>
                <SelectValue placeholder="Добавить соисполнителя" />
              </SelectTrigger>
              <SelectContent className={blockCreateTheme.selectContent}>
                {employees
                  .filter((e) => canAssignTo(e.id) && e.id !== newBlock.assigneeId && !newBlock.coAssigneeIds.includes(e.id))
                  .map((e) => (
                    <SelectItem key={e.id} value={e.id.toString()} className="hover:bg-purple-800/60 text-purple-200">
                      {e.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Наблюдатели (не обязательно)</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {newBlock.observerIds.map((obsId) => {
                const emp = employees.find(e => e.id === obsId)
                return (
                  <Badge key={obsId} variant="secondary" className={blockCreateTheme.badgeObserver}>
                    {emp?.name}
                    <button onClick={() => removeObserver(obsId)} className="ml-2">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )
              })}
            </div>
            <Select onValueChange={(v) => addObserver(+v)}>
              <SelectTrigger className={blockCreateTheme.selectTrigger}>
                <SelectValue placeholder="Добавить наблюдателя" />
              </SelectTrigger>
              <SelectContent className={blockCreateTheme.selectContent}>
                {employees
                  .filter((e) => e.id !== userId && !newBlock.observerIds.includes(e.id))
                  .map((e) => (
                    <SelectItem key={e.id} value={e.id.toString()} className="hover:bg-purple-800/60 text-purple-200">
                      {e.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Срочность</Label>
            <Select onValueChange={(v) => setNewBlock({ ...newBlock, urgency: v as "urgent" | "planned" })}>
              <SelectTrigger className={blockCreateTheme.selectTrigger}>
                <SelectValue placeholder="Планово" />
              </SelectTrigger>
              <SelectContent className={blockCreateTheme.selectContent}>
                <SelectItem value="planned" className="hover:bg-purple-800/60 text-purple-200">Планово</SelectItem>
                <SelectItem value="urgent" className="hover:bg-purple-800/60 text-purple-200">Срочно</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Уровень сложности</Label>
            <Select onValueChange={(v) => setNewBlock({ ...newBlock, difficulty: v as "easy" | "medium" | "hard" })}>
              <SelectTrigger className={blockCreateTheme.selectTrigger}>
                <SelectValue placeholder="Средняя" />
              </SelectTrigger>
              <SelectContent className={blockCreateTheme.selectContent}>
                <SelectItem value="easy" className="hover:bg-purple-800/60 text-purple-200">Легкая</SelectItem>
                <SelectItem value="medium" className="hover:bg-purple-800/60 text-purple-200">Средняя</SelectItem>
                <SelectItem value="hard" className="hover:bg-purple-800/60 text-purple-200">Сложная</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Напоминание</Label>
            <Input
              value={newBlock.reminderInterval}
              onChange={(e) => setNewBlock({ ...newBlock, reminderInterval: e.target.value })}
              placeholder="например: 1 день до дедлайна"
              className={blockCreateTheme.formInput}
            />
          </div>

          <div>
            <Label>Срок исполнения (дедлайн)</Label>
            <Input
              type="date"
              value={newBlock.deadline}
              onChange={(e) => setNewBlock({ ...newBlock, deadline: e.target.value })}
              className={blockCreateTheme.formInput}
            />
          </div>

          <div>
            <Label>Прикрепить файл</Label>
            <label className="cursor-pointer block">
              <input
                type="file"
                className="hidden"
                onChange={(e) => setNewBlockFile(e.target.files?.[0] || null)}
              />
              <div className={blockCreateTheme.fileUploadArea}>
                <Paperclip className="h-5 w-5" />
                <span className="text-sm">
                  {newBlockFile ? newBlockFile.name : "Выбрать файл"}
                </span>
              </div>
            </label>
            {newBlockFile && (
              <button
                onClick={() => setNewBlockFile(null)}
                className="text-red-400 text-sm mt-2 block"
              >
                Удалить файл
              </button>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button onClick={createBlock} className={blockCreateTheme.createButton}>
            Создать блок
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}